'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ChevronRight, Loader2, Brain } from 'lucide-react'
import { Markdown } from './markdown'

interface ReasoningProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultOpen?: boolean
  isStreaming?: boolean
}

const ReasoningContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
  isStreaming?: boolean
} | null>(null)

export function Reasoning({
  children,
  defaultOpen = false,
  isStreaming,
  className,
  ...props
}: ReasoningProps) {
  const [open, setOpen] = React.useState(defaultOpen)

  // Auto-open when streaming starts
  React.useEffect(() => {
    if (isStreaming) {
      setOpen(true)
    }
  }, [isStreaming])

  return (
    <ReasoningContext.Provider value={{ open, setOpen, isStreaming }}>
      <div
        className={cn('group w-full rounded-lg border bg-card text-card-foreground shadow-sm', className)}
        {...props}
      >
        {children}
      </div>
    </ReasoningContext.Provider>
  )
}

export function ReasoningTrigger({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const context = React.useContext(ReasoningContext)
  if (!context) throw new Error('ReasoningTrigger must be used within Reasoning')
  const { open, setOpen, isStreaming } = context

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-muted/50 transition-colors',
        className
      )}
      onClick={() => setOpen(!open)}
      {...props}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isStreaming ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="p-1 rounded-md bg-muted text-muted-foreground">
            <Brain className="h-3.5 w-3.5" />
          </div>
        )}
        <span className="text-sm font-medium truncate">
          {isStreaming ? 'Thinking...' : 'Reasoning'}
        </span>
      </div>

      <ChevronRight
        className={cn(
          'h-4 w-4 text-muted-foreground transition-transform duration-200',
          open && 'rotate-90'
        )}
      />
    </div>
  )
}

interface ReasoningContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

export function ReasoningContent({ children, className, ...props }: ReasoningContentProps) {
  const context = React.useContext(ReasoningContext)
  if (!context) throw new Error('ReasoningContent must be used within Reasoning')
  const { open } = context

  if (!open) return null

  return (
    <div className={cn('px-4 pb-3 pt-0 border-t bg-muted/30', className)} {...props}>
      <div className="pt-3">
        {typeof children === 'string' ? (
          <Markdown content={children} className="text-xs text-muted-foreground" />
        ) : (
          children
        )}
      </div>
    </div>
  )
}
